import { PanelType, Stack, StackItem } from '@fluentui/react';
import { useBoolean, useConst } from '@fluentui/react-hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NotificationsTarget, useNotificationsManager } from '../../contexts/notificationContext.js';
import { Data } from '../../data/Data.jsx';
import { DataIntervenantEXTPersonne } from '../../data/DataIntervenantEXTPersonne.jsx';
import { MaxLengths } from '../../data/MaxLenghts';
import { Immocom } from '../../specifique/immocom';
import { ComboBoxAgence } from '../ComboBox/ComboBoxAgence.jsx';
import ComboBoxCategorieIntervenantEXTPersonne from '../ComboBox/ComboBoxCategorieIntervenantEXTPersonne.jsx';
import { ComboBoxCivilite } from '../ComboBox/ComboBoxCivilite';
import ComboBoxIntervenantEXTSociete from '../ComboBox/ComboBoxIntervenantEXTSociete.jsx';
import { ComboBoxRegion } from '../ComboBox/ComboBoxRegion.jsx';
import EUIIconButton from '../EUI/EUIIconButton.jsx';
import { EUIPanel } from '../EUI/EUIPanel.jsx';
import { EUITextField } from '../EUI/EUITextField';
import FicheCategorieIntervenantEXTPersonne from './FicheCategorieIntervenantEXTPersonne.jsx';
import FicheIntervenantEXTSociete from './FicheIntervenantEXTSociete.jsx';

const FicheIntervenantEXTPersonne = (props) => {

    // CONTEXTS
    const { clear, dismiss, extract, load } = useNotificationsManager();

    // PROPS
    const {
        idCategorieIntervenant = undefined, // Permet une présélection de la catégorie
        idIntervenantEXTPersonne = undefined,
        idIntervenantEXTSociete = undefined, // Permet une présélection de la société
        isOpen = false,
        /* --- */
        onDismiss = () => null,
        onSave = () => null,
        onUpdateListCategorieIntervenantEXTPersonne = () => null,
        onUpdateListIntervenantEXTSociete = () => null,
    } = props;

    // CONSTANTES
    const _defaultIntervenantEXTPersonne = useConst({});

    // STATES
    const [_isFormValid, { setFalse: _setFormIsNotValid, setTrue: _setFormIsValid }] = useBoolean(false);

    const [_afficherPanelCategorieIntervenantEXTPersonne, _setAfficherPanelCategorieIntervenantEXTPersonne] = useState(false);
    const [_afficherPanelIntervenantEXTSociete, _setAfficherPanelIntervenantEXTSociete] = useState(false);
    const [_intervenantEXTPersonne, _setIntervenantEXTPersonne] = useState({ ..._defaultIntervenantEXTPersonne });
    const [_stamps, _setStamps] = useState({}); // Utilisé pour forcer la mise à jour des données

    // REFS
    const _isMounted = useRef(false); // Utilisé pour bloquer certains useEffect au premier render

    // METHODS
    const _chargementDonnees = useCallback(() => {
        const id = load("Chargement de l'intervenant en cours...");
        DataIntervenantEXTPersonne._detailler(idIntervenantEXTPersonne).then(response => {
            const onSuccess = () => {
                const intervenant = response.reponseAPIobjet;
                _setIntervenantEXTPersonne(intervenant);
            };
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, [idIntervenantEXTPersonne]);

    const _resetState = useCallback(() => {
        _setIntervenantEXTPersonne({ ..._defaultIntervenantEXTPersonne });
    }, []);

    const _onDismiss = useCallback(() => {
        onDismiss();
    }, [onDismiss]);

    const _onOpen = useCallback(() => {
        _resetState();
        if (!!idIntervenantEXTPersonne) _chargementDonnees();
        else {
            if (!!idCategorieIntervenant) _setIntervenantEXTPersonne(prev => ({ ...prev, idCategorieIntervenant }))
            if (!!idIntervenantEXTSociete) _setIntervenantEXTPersonne(prev => ({ ...prev, idIntervenantEXTSociete }))
        }
    }, [_resetState, idCategorieIntervenant, idIntervenantEXTPersonne, idIntervenantEXTSociete]);

    const _onSave = useCallback(() => {
        const id = load("Enregistrement du intervenant en cours...");
        DataIntervenantEXTPersonne._enregistrer(_intervenantEXTPersonne).then(response => {
            const onSuccess = () => {
                onSave();
                _resetState();
            }
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
            dismiss(id);
        });
    }, [_intervenantEXTPersonne, _resetState, idIntervenantEXTPersonne, onSave]);

    const _onVerificationFormulaire = useCallback(() => {
        clear();
        _setFormIsNotValid();
        Data._onValidationFormulaire("IntervenantEXTPersonne", _intervenantEXTPersonne, !!idIntervenantEXTPersonne ? "Modification" : "Création").then(response => {
            const onSuccess = _setFormIsValid;
            const onError = (result) => extract(result, NotificationsTarget.panels);
            Immocom.checkFetchResponse(response, onSuccess, onError);
        });
    }, [_intervenantEXTPersonne, idIntervenantEXTPersonne]);

    // EFFECTS (ComponentDidUpdate)
    // Vérification des données du formulaire
    useEffect(() => {
        if (_isMounted.current)
            _onVerificationFormulaire();
    }, [_isMounted, _intervenantEXTPersonne]);

    // EFFECTS (ComponentDidMount)
    useEffect(() => {
        _isMounted.current = true;
    }, []);

    // RENDER
    return <EUIPanel
        headerText={!!idIntervenantEXTPersonne ? "Modification d'un intervenant" : "Nouvel intervenant"}
        isFormValid={_isFormValid}
        isOpen={isOpen}
        onDismiss={_onDismiss}
        onOpen={_onOpen}
        onSave={_onSave}
        type={PanelType.medium}
    >

        <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
            <StackItem grow>
                <ComboBoxCategorieIntervenantEXTPersonne
                    label="Catégorie"
                    onChange={(event, option) => _setIntervenantEXTPersonne(prev => ({ ...prev, idCategorieIntervenant: option.key }))}
                    selectedKey={_intervenantEXTPersonne.idCategorieIntervenant}
                    required
                    stamp={_stamps.categorieIntervenantEXTPersonne}
                />
            </StackItem>
            <EUIIconButton
                iconProps={{ iconName: "Add" }}
                onClick={() => {
                    if (!!_intervenantEXTPersonne.idCategorieIntervenant)
                        _setIntervenantEXTPersonne(prev => ({ ...prev, idCategorieIntervenant: undefined }))
                    _setAfficherPanelCategorieIntervenantEXTPersonne(true);
                }}
                title="Créer catégorie intervenant"
            />
            <EUIIconButton iconProps={{ iconName: "Edit" }} title="Modifier catégorie intervenant" disabled={!_intervenantEXTPersonne.idCategorieIntervenant} onClick={() => _setAfficherPanelCategorieIntervenantEXTPersonne(true)} />
        </Stack>

        <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
            <StackItem grow>
                <ComboBoxIntervenantEXTSociete
                    label="Société"
                    onChange={(event, option) => _setIntervenantEXTPersonne(prev => ({ ...prev, idIntervenantEXTSociete: option.key }))}
                    selectedKey={_intervenantEXTPersonne.idIntervenantEXTSociete}
                    stamp={_stamps.intervenantEXTSociete}
                />
            </StackItem>
            <EUIIconButton
                iconProps={{ iconName: "Add" }}
                onClick={() => {
                    if (!!_intervenantEXTPersonne.idIntervenantEXTSociete)
                        _setIntervenantEXTPersonne(prev => ({ ...prev, idIntervenantEXTSociete: undefined }))
                    _setAfficherPanelIntervenantEXTSociete(true);
                }}
                title="Créer intervenant (société)"
            />
            <EUIIconButton iconProps={{ iconName: "Edit" }} title="Modifier intervenant (société)" disabled={!_intervenantEXTPersonne.idIntervenantEXTSociete} onClick={() => _setAfficherPanelIntervenantEXTSociete(true)} />
        </Stack>

        <ComboBoxCivilite
            onChange={(event, option) => _setIntervenantEXTPersonne(prev => ({ ...prev, civilite: option.key }))}
            required
            selectedKey={_intervenantEXTPersonne.civilite}
        />

        <EUITextField
            label="Nom"
            maxLength={MaxLengths.intervenantEXTPersonne.nom}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTPersonne(prev => ({ ...prev, nom: newValue }))}
            required
            value={_intervenantEXTPersonne.nom}
        />

        <EUITextField
            label="Prénom"
            maxLength={MaxLengths.intervenantEXTPersonne.prenom}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTPersonne(prev => ({ ...prev, prenom: newValue }))}
            value={_intervenantEXTPersonne.prenom}
        />

        <EUITextField
            label="Fonction"
            maxLength={MaxLengths.intervenantEXTPersonne.fonction}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTPersonne(prev => ({ ...prev, fonction: newValue }))}
            value={_intervenantEXTPersonne.fonction}
        />

        <ComboBoxRegion
            onChange={(event, option) => _setIntervenantEXTPersonne(prev => ({ ...prev, idRegion: option.key, idAgence: undefined }))}
            selectedKey={_intervenantEXTPersonne.idRegion}
        />

        <ComboBoxAgence
            idRegion={_intervenantEXTPersonne.idRegion}
            onChange={(event, option) => _setIntervenantEXTPersonne(prev => ({ ...prev, idAgence: option.key }))}
            selectedKey={_intervenantEXTPersonne.idAgence}
        />

        <EUITextField
            label="Téléphone fixe"
            maxLength={MaxLengths.intervenantEXTPersonne.tel}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTPersonne(prev => ({ ...prev, tel: newValue }))}
            required
            value={_intervenantEXTPersonne.tel}
        />

        <EUITextField
            label="Portable"
            maxLength={MaxLengths.intervenantEXTPersonne.portable}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTPersonne(prev => ({ ...prev, portable: newValue }))}
            required
            value={_intervenantEXTPersonne.portable}
        />

        <EUITextField
            label="Email"
            maxLength={MaxLengths.intervenantEXTPersonne.email}
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTPersonne(prev => ({ ...prev, email: newValue }))}
            required
            value={_intervenantEXTPersonne.email}
        />

        <EUITextField
            label="Remarque"
            maxLength={MaxLengths.intervenantEXTPersonne.remarque}
            multiline
            onChange={_setFormIsNotValid}
            onDelayedChange={(event, newValue) => _setIntervenantEXTPersonne(prev => ({ ...prev, remarque: newValue }))}
            rows={4}
            value={_intervenantEXTPersonne.remarque}
        />

        <FicheCategorieIntervenantEXTPersonne
            idCategorieIntervenantEXTPersonne={_intervenantEXTPersonne.idCategorieIntervenant}
            isOpen={_afficherPanelCategorieIntervenantEXTPersonne}
            onDismiss={() => _setAfficherPanelCategorieIntervenantEXTPersonne(false)}
            onSave={() => {
                _setAfficherPanelCategorieIntervenantEXTPersonne(false)
                _setStamps(prev => ({ ...prev, categorieIntervenantEXTPersonne: Date.now() }));
                if (typeof onUpdateListCategorieIntervenantEXTPersonne === "function") onUpdateListCategorieIntervenantEXTPersonne();
            }}
        />

        <FicheIntervenantEXTSociete
            idIntervenantEXTSociete={_intervenantEXTPersonne.idIntervenantEXTSociete}
            isOpen={_afficherPanelIntervenantEXTSociete}
            onDismiss={() => _setAfficherPanelIntervenantEXTSociete(false)}
            onSave={() => {
                _setAfficherPanelIntervenantEXTSociete(false)
                _setStamps(prev => ({ ...prev, intervenantEXTSociete: Date.now() }));
                if (typeof onUpdateListIntervenantEXTSociete === "function") onUpdateListIntervenantEXTSociete();
            }}
        />

    </EUIPanel>;

};

export default FicheIntervenantEXTPersonne;